import axios from "axios";
import { getCookie } from "../utils/cookieUtils";
import { ConnectAccountResponse } from "./stripeAccountTypes";
import { BankAccountResponse } from "./bankAccountTypes";

const stripeApiBaseUrl = import.meta.env.VITE_STRIPE_API;

const stripeApi = axios.create({
  baseURL: stripeApiBaseUrl,
});

stripeApi.interceptors.request.use((config) => {
  const token = getCookie("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CreatePaymentIntentResponse {
  paymentIntentID: string;
  clientSecret: string;
}

export interface ConfirmPaymentResponse {
  status: string;
  paymentId: string;
  clientSecret: string;
}

export interface SavePaymentMethodResponse {
  id: string;
}

export interface RefundPaymentResponse {
  refundID: string;
  status: string;
  paymentId: string;
}

// New interface for starting Connect Onboarding
export interface StartConnectOnboardingResponse {
  url: string;
}

// Existing endpoints remain unchanged.
export const createPayment = async (data: {
  user_id: string;
  amount: number;
  currency: string;
  payment_type: string;
  metadata: Record<string, unknown>;
}): Promise<CreatePaymentIntentResponse> => {
  try {
    const response = await stripeApi.post("/api/payment/create", data);
    console.log("the response:", response);
    return response.data;
  } catch (error) {
    console.error("Error creating payment", error);
    throw new Error("Failed to create payment");
  }
};

export const confirmPayment = async (
  payment_intent_id: string
): Promise<ConfirmPaymentResponse> => {
  try {
    const response = await stripeApi.post("/api/payment/confirm", {
      payment_intent_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error confirming payment", error);
    throw new Error("Failed to confirm payment");
  }
};

export const savePaymentMethod = async (data: {
  user_id: string;
  payment_method_id: string;
}): Promise<SavePaymentMethodResponse> => {
  try {
    const response = await stripeApi.post("/api/payment-method/save", data);
    return response.data;
  } catch (error) {
    console.error("Error saving payment method", error);
    throw new Error("Failed to save payment method");
  }
};

export const refundPayment = async (data: {
  payment_intent_id: string;
  amount?: number;
}): Promise<RefundPaymentResponse> => {
  try {
    const response = await stripeApi.post("/api/payment/refund", data);
    return response.data;
  } catch (error) {
    console.error("Error refunding payment", error);
    throw new Error("Failed to refund payment");
  }
};

// Updated connect onboarding function.
export const startConnectOnboarding = async (data: {
  user_id: string;
  email: string;
  country: string;
}): Promise<StartConnectOnboardingResponse> => {
  try {
    const response = await stripeApi.post("/api/connect/onboarding", data);
    return response.data;
  } catch (error) {
    console.error("Error starting connect onboarding", error);
    throw new Error("Failed to start connect onboarding");
  }
};

// New function: Get Connected Account by User ID.
export const getConnectedAccount = async (
  user_id: string
): Promise<ConnectAccountResponse> => {
  try {
    const response = await stripeApi.get("/api/connect/account", {
      params: { user_id },
    });
    return response.data;
  } catch (error) {
    console.error("Error retrieving connected account", error);
    throw new Error("Failed to retrieve connected account");
  }
};

// New function: Get Bank Account by User ID.
export const getBankAccount = async (
  user_id: string
): Promise<BankAccountResponse> => {
  try {
    const response = await stripeApi.get("/api/connect/bankaccount", {
      params: { user_id },
    });
    return response.data;
  } catch (error) {
    console.error("Error retrieving bank account", error);
    throw new Error("Failed to retrieve bank account");
  }
};
